import { create } from 'zustand';
import {
  TAffiliateAccountProductLink,
  TOnboardingAccount,
  TOnboardingPayment,
  TPackages,
} from '@src/types';

export type PaymentFormData = {
  coupon_code: string;
  first_name: string;
  last_name: string;
  referral_source: string;
  payment_type: string;
  street: string;
  city: string;
  country: string;
  state: string;
  zip_code: string;
  phone_number: string;
};

export const TAddOnsOther = {
  weeklyAddOns: 49.99,
  standardPayout: 0,
  expressPayout: 29.99,
  instantPayout: 49.99,
  resetDiscount: 39.99,
  noMinDaysToQualify: 19.99,
  extraFirstPayout: 24.99,
};

const PURCHASE_MODE_KEY = 'checkout-purchase-mode';

export const getPurchaseMode = (): boolean => {
  return sessionStorage.getItem(PURCHASE_MODE_KEY) === 'true';
};

export const setPurchaseModeStorage = (value: boolean): void => {
  if (value) {
    sessionStorage.setItem(PURCHASE_MODE_KEY, 'true');
  } else {
    sessionStorage.removeItem(PURCHASE_MODE_KEY);
  }
};

export const clearPurchaseMode = (): void => {
  sessionStorage.removeItem(PURCHASE_MODE_KEY);
};

interface CheckoutState {
  platform?: string;
  sizeIndex?: number;
  typeIndex?: number;
  sizeType?: string;
  isCompetitionAccount?: boolean;
  isPurchaseMode: boolean;
  affiliateRef?: string;
  affiliateProductCode?: string;
  affiliateProductData?: TAffiliateAccountProductLink;
  payment: TOnboardingPayment | null;
  account: TOnboardingAccount | null;
  addOns: { [addonType: string]: string | string[] };
  packages: TPackages[] | null;
  paymentFormData: Partial<PaymentFormData>;
  setPackages: (packages: TPackages[] | null) => void;
  setPlatform: (platform: string) => void;
  setIsCompetitionAccount: (isCompetitionAccount: boolean) => void;
  setIsPurchaseMode: (isPurchaseMode: boolean) => void;
  setAffiliateRef: (ref: string) => void;
  setAffiliateProductCode: (productCode: string) => void;
  setAffiliateProductData: (data: any) => void;
  setAddOns: (v: { [addonType: string]: string | string[] }) => void;
  setSizeIndex: (sizeIndex?: number) => void;
  setTypeIndex: (typeIndex?: number) => void;
  setPayment: (payment: TOnboardingPayment) => void;
  setAccount: (account: TOnboardingAccount | null) => void;
  setPaymentFormData: (data: Partial<PaymentFormData>) => void;
  reset: () => void;
}

export const useCheckoutStore = create<CheckoutState>()((set) => {
  return {
    platform: undefined,
    accountType: null,
    account: null,
    payment: null,
    packages: null,
    paymentFormData: {},
    // Key: addon_type (string), Value: selected item uid or array of uids
    // Static item uids: "${addonType}__s_${idx}" (data) / "${addonType}__s2_${idx}" (data2)
    // API item uids: "api_${item.id}" (data) / "api2_${item.id}" (data2)
    addOns: {
      default: 'default__s_0',                          // Monthly subscription
      account_bundle: 'account_bundle__s_0',            // No, thanks! Keep only 1 account
      drawdown: ['drawdown__s_0', 'drawdown__s2_0'],    // Trailing Drawdown + $0 part
      pa_payout_x_days: 'pa_payout_x_days__s_0',       // Last Saturday monthly
      performance_account_fee: '',                       // API-driven, no default
      eval_discount_x_reset: 'eval_discount_x_reset__s_0', // No Thanks
      eval_quality_x_days: 'eval_quality_x_days__s_0', // Standard 10 Days
    },
    isPurchaseMode: getPurchaseMode(),
    affiliateRef: undefined,
    affiliateProductCode: undefined,
    affiliateProductData: undefined,
    setPlatform: (platform) => set((state) => ({ ...state, platform })),
    setPackages: (packages) => set({ packages }),
    setAddOns: (addOns) => set({ addOns }),
    setSizeIndex: (sizeIndex) => set((state) => ({ ...state, sizeIndex })),
    setTypeIndex: (typeIndex) => set((state) => ({ ...state, typeIndex })),
    setAccountType: (sizeType: string) =>
      set((state) => ({ ...state, sizeType })),
    setAccount: (account) => set((state) => ({ ...state, account })),
    setPayment: (payment) => set((state) => ({ ...state, payment })),
    setPaymentFormData: (paymentFormData) =>
      set((state) => ({ ...state, paymentFormData })),
    setIsCompetitionAccount: (isCompetitionAccount) => {
      set((state) => ({ ...state, isCompetitionAccount }));
    },
    setIsPurchaseMode: (isPurchaseMode) => {
      setPurchaseModeStorage(isPurchaseMode);
      set((state) => ({ ...state, isPurchaseMode }));
    },
    setAffiliateRef: (affiliateRef) => {
      set((state) => ({ ...state, affiliateRef }));
    },
    setAffiliateProductCode: (affiliateProductCode) => {
      set((state) => ({ ...state, affiliateProductCode }));
    },
    setAffiliateProductData: (affiliateProductData) => {
      set((state) => ({ ...state, affiliateProductData }));
    },
    reset: () => {
      clearPurchaseMode();
      set((state) => {
        return {
          ...state,
          platform: '',
          payment: null,
          account: null,
          paymentFormData: {},
          isPurchaseMode: false,
          affiliateRef: undefined,
          affiliateProductCode: undefined,
          affiliateProductData: undefined,
        };
      });
    },
  };
});
